import { useAtom } from 'jotai';
import { orderBy } from 'lodash';
import { useMemo } from 'react';

import {
  PRIORITY_COLORS,
  STATUS_COLORS
} from '@/constants/chartcolors.constants';
import { PRIORITY } from '@/constants/common';
import {
  useFetchGroupByOptions,
  useFetchThreatTicketsByDate,
  useFetchV2Tickets
} from '@/pages/Tickets/Tickets.store';

import { selectedOrganizationAtom } from '../Organizations';
import { TICKET_THREAT_RESOLUTIONS } from '../Ticket';
import dashboardTimeFrameAtom, {
  basicSearchParamsAtom,
  ticketSearchParamsAtom
} from './Dashboard.store';
import {
  DASHBOARD_SERVICE_TICKET_GROUP_BY_FIELDS,
  DASHBOARD_THREAT_TICKET_GROUP_BY_FIELDS,
  DASHBOARD_TICKET_GROUP_BY_FIELDS,
  OPEN_TICKET_STATUS,
  TICKET_QUERY_PARAMS
} from './dashboard.constants';

const useDashboardV2Tickets = () => {
  const [selectedOrg] = useAtom(selectedOrganizationAtom);
  const [dashboardTimeFrame] = useAtom(dashboardTimeFrameAtom);
  const [basicSearchParams] = useAtom(basicSearchParamsAtom);
  const [ticketSearchParams] = useAtom(ticketSearchParamsAtom);

  // ------- Service Tickets

  const allServiceSearchParams = useMemo(() => {
    return {
      ...basicSearchParams,
      ticket_type__in: [2]
    };
  }, [basicSearchParams, dashboardTimeFrame]);

  const { data: serviceGroupedData, isFetching: isFetchingServiceGroupedData } =
    useFetchGroupByOptions(
      DASHBOARD_SERVICE_TICKET_GROUP_BY_FIELDS,
      allServiceSearchParams
    );

  // --------

  // ------- Threat Tickets

  const allThreatSearchParams = useMemo(() => {
    return {
      ...basicSearchParams,
      ticket_type__in: [1]
    };
  }, [basicSearchParams]);

  const { data: threatGroupedData, isFetching: isFetchingThreatGroupedData } =
    useFetchGroupByOptions(
      DASHBOARD_THREAT_TICKET_GROUP_BY_FIELDS,
      allThreatSearchParams
    );
  // --------

  // ------ Threat Categories
  const threatCategoriesBarData = useMemo(() => {
    return orderBy(
      threatGroupedData?.ticket_category__name || [],
      ['count'],
      ['desc']
    )
      ?.map((option) => {
        return {
          name: option.label,
          type: 'count',
          data: [option.count || 0],
          count: option.count || 0,
          color: '#008ffb'
        };
      })
      .slice(0, 10); // Take top 10 categories
  }, [threatGroupedData]);
  // --------

  // ----- Service Categories

  const serviceCategoriesBarData = useMemo(() => {
    return orderBy(
      serviceGroupedData?.ticket_category__name || [],
      ['count'],
      ['desc']
    )
      ?.map((option) => {
        return {
          name: option.label,
          type: 'count',
          data: [option.count || 0],
          count: option.count || 0,
          color: '#008ffb'
        };
      })
      .slice(0, 10); // Take top 10 categories
  }, [serviceGroupedData]);
  // --------

  // ---- Threat Resolutions

  const threatResolutionBarData = useMemo(() => {
    return orderBy(
      TICKET_THREAT_RESOLUTIONS.map((resolution) => {
        const groupResolvedCount =
          threatGroupedData?.resolution_id?.find(
            (group) => Number(group.value) === resolution.id
          )?.count || 0;
        return {
          name: resolution.text,
          data: [groupResolvedCount],
          count: groupResolvedCount
        };
      }),
      ['count'],
      ['desc']
    );
  }, [threatGroupedData]);
  //---------

  // ---- Threat Status
  const threatStatusDonutData = useMemo(() => {
    return threatGroupedData?.ticket_status__name?.map((group) => {
      const color =
        STATUS_COLORS[group.value as keyof typeof STATUS_COLORS] || '#008ffb';
      return {
        name: group.label,
        count: group.count,
        color
      };
    });
  }, [threatGroupedData]);
  // --------

  // ---- Service Status
  const serviceStatusDonutData = useMemo(() => {
    return serviceGroupedData?.ticket_status__name?.map((group) => {
      const color =
        STATUS_COLORS[group.value as keyof typeof STATUS_COLORS] || '#008ffb';
      return {
        name: group.label,
        count: group.count,
        color
      };
    });
  }, [serviceGroupedData]);
  // --------

  // ---- Threat percentage bar data

  const threatPercentageBarData = useMemo(() => {
    return Object.entries(PRIORITY)?.map(([key, value]) => {
      const count =
        threatGroupedData?.ticket_priority__name?.find(
          (option) => option.label === value
        )?.count || 0;

      return {
        name: value,
        type: 'count',
        data: [count],
        color: PRIORITY_COLORS[key as keyof typeof PRIORITY_COLORS] || '#008ffb'
      };
    });
  }, [threatGroupedData]);
  // --------

  // Top threat tickets

  const threatSearchParams = useMemo<Record<string, string | string[]>>(() => {
    let params: Record<string, string | string[]> = {
      ...ticketSearchParams,
      ticket_type__in: ['1']
    };

    if (selectedOrg.id !== 0) {
      params = {
        ...params,
        organization__label: selectedOrg.label
      };
    }

    return params;
  }, [ticketSearchParams, selectedOrg]);

  const { data: topThreatTickets, isFetching: isFetchingTopThreatTickets } =
    useFetchV2Tickets({
      enabled: true,
      queryParams: TICKET_QUERY_PARAMS,
      searchParams: threatSearchParams
    });

  // ------

  // Top service tickets

  const serviceSearchParams = useMemo<Record<string, string | string[]>>(() => {
    let params: Record<string, string | string[]> = {
      ...ticketSearchParams,
      ticket_type__in: ['2']
    };

    if (selectedOrg.id !== 0) {
      params = {
        ...params,
        organization__label: selectedOrg.label
      };
    }

    return params;
  }, [ticketSearchParams, selectedOrg]);

  const { data: topServiceTickets, isFetching: isFetchingTopServiceTickets } =
    useFetchV2Tickets({
      enabled: true,
      queryParams: TICKET_QUERY_PARAMS,
      searchParams: serviceSearchParams
    });

  // ------

  // Threat tickets by date and priority

  const {
    data: threatTicketsByDate,
    isFetching: isFetchingThreatTicketsByDate
  } = useFetchThreatTicketsByDate();

  // ------------

  // Note: percentageBarData is identical to threatPercentageBarData
  const percentageBarData = threatPercentageBarData;

  // Get and calculate values for small cards
  const highPriorityTicketsSearchParams = useMemo(() => {
    return {
      ...basicSearchParams,
      ticket_status__name__in: OPEN_TICKET_STATUS,
      ticket_priority__name__in: ['P1', 'P2'],
      ticket_type__in: ['1'] // Threat tickets only
    };
  }, [basicSearchParams]);

  const { data: highPriorityThreatTicketsGroupBy } = useFetchGroupByOptions(
    DASHBOARD_TICKET_GROUP_BY_FIELDS,
    highPriorityTicketsSearchParams
  );

  const highPriorityThreatTicketsCount = useMemo(() => {
    return Object.values(
      highPriorityThreatTicketsGroupBy?.ticket_priority__name || {}
    )
      .map((value) => {
        const count = value.count || 0;
        return count;
      })
      .reduce((acc, count) => acc + count, 0);
  }, [highPriorityThreatTicketsGroupBy]);

  const openThreatTicketsCount = useMemo(() => {
    return (
      threatGroupedData?.ticket_status__name?.find(
        (option) => option.label === 'Open'
      )?.count || 0
    );
  }, [threatGroupedData]);

  const acknowledgedThreatTicketsCount = useMemo(() => {
    return (
      threatGroupedData?.ticket_status__name?.find(
        (option) => option.label === 'Acknowledged'
      )?.count || 0
    );
  }, [threatGroupedData]);

  const unresolvedThreatTicketsCount = useMemo(() => {
    return (
      (openThreatTicketsCount || 0) + (acknowledgedThreatTicketsCount || 0)
    );
  }, [openThreatTicketsCount, acknowledgedThreatTicketsCount]);

  return {
    percentageBarData,
    threatResolutionBarData: threatResolutionBarData ?? [],
    threatCategoriesBarData: threatCategoriesBarData ?? [],
    serviceCategoriesBarData: serviceCategoriesBarData ?? [],
    threatStatusDonutData: threatStatusDonutData ?? [],
    serviceStatusDonutData: serviceStatusDonutData ?? [],
    threatPercentageBarData: threatPercentageBarData ?? [],
    topServiceTickets,
    topThreatTickets,
    highPriorityThreatTicketsCount,
    unresolvedThreatTicketsCount,
    openThreatTicketsCount,
    threatTicketsByDate,
    isFetchingServiceGroupedData,
    isFetchingThreatGroupedData,
    isFetchingTopThreatTickets,
    isFetchingTopServiceTickets,
    isFetchingThreatTicketsByDate
  };
};

export default useDashboardV2Tickets;
